import React, { useState, useEffect } from 'react';
import '../styles/Form.css'; // CSSファイルをインポート
import { useRecaptcha } from '../hooks/useRecaptcha';

const GroupLessonForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    inquiry: '',  // お問い合わせフィールドを追加
    consent: false,
    ipAddress: '',
    sourceLink: '',
    timezone: '',
  });

  const [formErrors, setFormErrors] = useState({});
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [recaptchaToken, recaptchaReady] = useRecaptcha(); // recaptcha用のフック

  // IPアドレス、リファラー、タイムゾーンの取得
  useEffect(() => {
    // IPアドレスの取得
    fetch('https://ipinfo.io/json')
      .then(response => response.json())
      .then(data => {
        setFormData(prevData => ({
          ...prevData,
          ipAddress: data.ip,
        }));
      })
      .catch(error => console.error('Error fetching IP address:', error));

    // リファラーの取得
    setFormData(prevData => ({
      ...prevData,
      sourceLink: document.referrer,
    }));

    // タイムゾーンの取得
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    setFormData(prevData => ({
      ...prevData,
      timezone: timezone,
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = () => {
    let errors = {};
    if (!formData.firstName) errors.firstName = '名（ローマ字）を入力してください';
    if (!formData.lastName) errors.lastName = '姓（ローマ字）を入力してください';
    if (!formData.email) errors.email = '正しいメールアドレスを入力してください';
    if (!formData.consent) errors.consent = 'プライバシーポリシーに同意してください';

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateConsent = () => {
    if (!formData.consent) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        consent: 'プライバシーポリシーに同意してください',
      }));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isFormValid = validateForm();
    const isConsentValid = validateConsent();

    if (!isFormValid || !isConsentValid) {
      setShowErrorMessage(true);
      return;
    }

    if (!recaptchaReady || !recaptchaToken) {
      alert('reCAPTCHAの検証に失敗しました。再度お試しください。');
      return;
    }

    try {
      // reCAPTCHA 検証リクエストをサーバーに送信
      const recaptchaResponse = await fetch('/wp-admin/admin-ajax.php?action=verify_recaptcha', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          recaptcha_token: recaptchaToken,
        }),
      });

      const recaptchaResult = await recaptchaResponse.json();

      if (!recaptchaResult.success) {
        alert(recaptchaResult.message || 'reCAPTCHAの検証に失敗しました。');
        return;
      }

      // メール通知の処理（バックエンドで対応）
      const response = await fetch('/wp-admin/admin-ajax.php?action=send_group_lesson_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData), // フォームデータを送信
      });

      const result = await response.json();

      if (result.success) {
        setShowErrorMessage(false);
        window.location.href = '/thankyou-grouplesson'; // フォーム送信後にリダイレクト
      } else {
        alert('送信中にエラーが発生しました。再度お試しください。');
      }
    } catch (error) {
      alert('reCAPTCHAの検証中にエラーが発生しました。');
    }
  };

  return (
    <form className="p-form" onSubmit={handleSubmit}>
      <div className="p-form__items">
        <div className={`p-form__item ${formErrors.lastName ? 'invalid' : ''}`}>
          <label className="p-form__head">姓（ローマ字）<span className="p-form__required">必須</span></label>
          <div className="p-form__body">
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleInputChange}
              placeholder="Yamada"
              className={formErrors.lastName ? 'invalid' : ''}
            />
            {formErrors.lastName && <span className="error invalid">{formErrors.lastName}</span>}
          </div>
        </div>

        <div className={`p-form__item ${formErrors.firstName ? 'invalid' : ''}`}>
          <label className="p-form__head">名（ローマ字）<span className="p-form__required">必須</span></label>
          <div className="p-form__body">
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleInputChange}
              placeholder="Taro"
              className={formErrors.firstName ? 'invalid' : ''}
            />
            {formErrors.firstName && <span className="error invalid">{formErrors.firstName}</span>}
          </div>
        </div>

        <div className={`p-form__item ${formErrors.email ? 'invalid' : ''}`}>
          <label className="p-form__head">Emailアドレス<span className="p-form__required">必須</span></label>
          <div className="p-form__body">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="example@email.com"
              className={formErrors.email ? 'invalid' : ''}
            />
            {formErrors.email && <span className="error invalid">{formErrors.email}</span>}
          </div>
        </div>

        {/* お問い合わせフィールドを追加 */}
        <div className="p-form__item">
          <label className="p-form__head">お問い合わせ</label>
          <div className="p-form__body">
            <textarea
              name="inquiry"
              value={formData.inquiry}
              onChange={handleInputChange}
              rows="4"
              placeholder="ご質問があればこちらにご記入ください"
            ></textarea>
          </div>
        </div>

        {/* プライバシーポリシーの同意チェックボックス */}
        <div className={`p-form__privacy ${formErrors.consent ? 'invalid' : ''}`}>
          <input
            type="checkbox"
            id="consent"
            name="consent"
            value="true"
            checked={formData.consent}
            onChange={handleInputChange}
            className="p-form__privacy-check"
          />
          <label htmlFor="consent" className="p-form__privacy-label">
            プライバシーポリシーに同意する
          </label>
        </div>
        <div className="p-form__privacy-link">
          {/* ラベルの下にリンクを表示 */}
          <a href="/policy" target="_blank" rel="noopener">
            「プライバシーポリシー」
          </a>
        </div>
        <div className="p-form__privacy-error">
          {/* エラーメッセージをラベルの下に表示 */}
          {formErrors.consent && <span className="error invalid">{formErrors.consent}</span>}
        </div>

        <input type="submit" className="p-form__send" value="送信する" />
      </div>
    </form>
  );
};

export default GroupLessonForm;

